/**
 * Part C / C3 UI (SPEC v0.9.3) — tropical cyclones whose current position or forecast
 * track comes within ~500 km of Thailand, from /api/storms (GDACS, EC JRC, CC BY 4.0,
 * aggregating JTWC). We render ONLY GDACS-provided geometry: observed track (solid) +
 * forecast track (dashed) in lavender, the uncertainty cone (purple), time-stepped
 * forecast positions (dots + Thai-day labels), and the current centre (owner-approved
 * storm icon). No storm near Thailand → nothing shown. No mock; never a track of our own.
 * Times are shown in Thai time as a day + part-of-day (เช้า/บ่าย/ค่ำ/กลางคืน), not UTC hours.
 */
import maplibregl, { type Map as MlMap, type GeoJSONSource } from "maplibre-gl";
import { thaiDayPart, thaiToday } from "@flowsight/sim";
import { publish, okDim, errDim } from "./summary-store";
import { ensureStormIcon } from "./storm-icon";

interface StormPos { lon: number; lat: number; time: string | null; windKmh: number | null; forecast: boolean }
interface Storm { id: number; name: string; alertLevel: string | null; category: string | null; nearestKm: number; status?: "near" | "watch" | "far"; current: [number, number] | null; observedPath: [number, number][]; forecastPath: [number, number][]; positions: StormPos[]; cone: number[][][]; updatedAt: string | null }
interface StormsResp { storms: Storm[]; source: string; license?: string; updatedAt?: string; scope?: "near" | "global" }

const TRACK = "#AFA9EC", CONE = "#7F77DD";
const THMON = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const DOW = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
const THBOX = { minLat: 5.6, maxLat: 20.5, minLon: 97.3, maxLon: 105.6 };

let pill: HTMLElement | null = null;
let current: StormsResp | null = null;
// the "พายุรอบโลก" panel uses a wider dataset (every cyclone on Earth) kept separate from `current`,
// so drawing the world view never contaminates the near-Thailand cache the summary/▶ timeline read.
let globalData: StormsResp | null = null;
let pillEnabled = true; // the "พายุรอบโลก" panel is its own list, so it draws the overlay without the pill
// v0.9.13 spin: the live current-centre icons rotate while the overlay is on (unless reduced motion)
const prefersReducedMotion = (): boolean => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
let curFeatures: GeoJSON.Feature[] = [];
let spinRaf = 0, spinAngle = 0, spinLast = 0;
const escHtml = (x: string): string => x.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));

export async function loadStorms(): Promise<StormsResp | null> {
  let d: StormsResp | null = null;
  try { const r = await fetch("/api/storms"); if (r.ok) d = (await r.json()) as StormsResp; } catch { d = null; }
  // v0.9.5 สรุป: storms are global — publish once for every point's summary
  if (!d) publish(0, "storm", errDim);
  else {
    const s0 = [...d.storms].sort((a, b) => a.nearestKm - b.nearestKm)[0] ?? null;
    // "affects" = the nearest storm is in the near band OR a track enters Thailand; a watch-only
    // list (all far, no Thailand report) publishes affects=false so the summary row stays calm.
    const affects = s0 ? (s0.status !== "watch" || entersTH(s0)) : false;
    publish(0, "storm", okDim({ count: d.storms.length, name: s0?.name ?? null, nearestKm: s0?.nearestKm ?? null, affects, updatedAt: d.updatedAt ?? null }));
  }
  // cache the fetched payload so the summary row, the ▶ timeline and the
  // "ดูสภาพอากาศ" button can use it even before the map overlay is drawn (v0.9.12)
  current = d;
  return d;
}

const empty = (): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features: [] });
const fc = (features: GeoJSON.Feature[]): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features });

/* ── Thai time + geometry helpers ───────────────────────────────────────── */
function parseLabel(label: string | null, refIso: string | null): Date | null {
  if (!label) return null;
  const m = label.match(/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2})/);
  if (!m) return null;
  const dd = +m[1]!, mm = +m[2]!, hh = +m[3]!, mi = +m[4]!;
  const ref = refIso ? new Date(refIso) : new Date();
  let y = ref.getUTCFullYear();
  const refM = ref.getUTCMonth() + 1;
  if (mm - refM > 6) y -= 1; else if (refM - mm > 6) y += 1; // year-boundary guard
  return new Date(Date.UTC(y, mm - 1, dd, hh, mi));
}
function thaiLabel(utc: Date): string {
  const t = new Date(utc.getTime() + 7 * 3600 * 1000);
  return `${DOW[t.getUTCDay()]} ${t.getUTCDate()} ${THMON[t.getUTCMonth()]} ${thaiDayPart(t.getUTCHours())}`;
}
const inTH = (lon: number, lat: number): boolean => lat >= THBOX.minLat && lat <= THBOX.maxLat && lon >= THBOX.minLon && lon <= THBOX.maxLon;
function dirFromThailand(lon: number, lat: number): string {
  const ang = (Math.atan2(lon - 100.5, lat - 13.5) * 180) / Math.PI; // 0=N, 90=E
  const dirs = ["เหนือ", "ตะวันออกเฉียงเหนือ", "ตะวันออก", "ตะวันออกเฉียงใต้", "ใต้", "ตะวันตกเฉียงใต้", "ตะวันตก", "ตะวันตกเฉียงเหนือ"];
  return dirs[Math.round((((ang % 360) + 360) % 360) / 45) % 8]!;
}
/** forecast positions grouped by Thai calendar day (for the day selector) */
function dayGroups(s: Storm): { thaiDate: string; label: string; pos: StormPos; enters: boolean }[] {
  const out: { thaiDate: string; label: string; pos: StormPos; enters: boolean }[] = [];
  const seen = new Set<string>();
  for (const p of s.positions) {
    const utc = parseLabel(p.time, s.updatedAt);
    if (!utc) continue;
    const thaiDate = new Date(utc.getTime() + 7 * 3600 * 1000).toISOString().slice(0, 10);
    if (seen.has(thaiDate)) continue; // first position of each Thai day
    seen.add(thaiDate);
    out.push({ thaiDate, label: thaiLabel(utc), pos: p, enters: inTH(p.lon, p.lat) });
  }
  return out.filter((g) => g.thaiDate >= thaiToday(new Date())); // today onward
}
/** true only when a forecast position actually enters the Thailand box — i.e. GDACS reports it
 *  reaching Thailand. Otherwise the UI states "ยังไม่มีรายงานเข้าไทย" (task #1705). */
const entersTH = (s: Storm): boolean => dayGroups(s).some((g) => g.enters);

export async function installStormLayers(map: MlMap): Promise<void> {
  await ensureStormIcon(map);
  if (map.getSource("storm-cone")) return;
  for (const id of ["storm-cone", "storm-obs", "storm-fore", "storm-pos", "storm-cur"]) map.addSource(id, { type: "geojson", data: empty() });
  map.addLayer({ id: "storm-cone-fill", type: "fill", source: "storm-cone", paint: { "fill-color": CONE, "fill-opacity": 0.22 } });
  map.addLayer({ id: "storm-cone-line", type: "line", source: "storm-cone", paint: { "line-color": CONE, "line-opacity": 0.5, "line-width": 1 } });
  map.addLayer({ id: "storm-obs-line", type: "line", source: "storm-obs", paint: { "line-color": TRACK, "line-width": 2.5 }, layout: { "line-cap": "round", "line-join": "round" } });
  map.addLayer({ id: "storm-fore-line", type: "line", source: "storm-fore", paint: { "line-color": TRACK, "line-width": 2.5, "line-dasharray": [2, 1.6] }, layout: { "line-cap": "round", "line-join": "round" } });
  map.addLayer({ id: "storm-pos-dot", type: "circle", source: "storm-pos", paint: { "circle-radius": 3.4, "circle-color": TRACK, "circle-stroke-color": "#05080f", "circle-stroke-width": 1 } });
  map.addLayer({ id: "storm-pos-label", type: "symbol", source: "storm-pos", layout: { "text-field": ["get", "label"], "text-font": ["Noto Sans Regular"], "text-size": 10, "text-offset": [0, -0.9], "text-anchor": "bottom", "text-optional": true, "text-allow-overlap": false }, paint: { "text-color": "#d9d5f5", "text-halo-color": "#05080f", "text-halo-width": 1.4 } });
  // v0.9.13 "พายุรอบโลก": the current-centre cyclone icon spins (icon-rotate driven by a rAF loop)
  // with the storm name beside it; the text label keeps its own upright orientation.
  map.addLayer({ id: "storm-cur-icon", type: "symbol", source: "storm-cur", layout: { "icon-image": "storm-icon", "icon-size": 0.58, "icon-rotate": ["get", "rot"], "icon-rotation-alignment": "map", "icon-allow-overlap": true, "icon-ignore-placement": true, "text-field": ["get", "label"], "text-font": ["Noto Sans Regular"], "text-size": 12, "text-offset": [0, 1.6], "text-anchor": "top", "text-optional": true }, paint: { "text-color": "#fff", "text-halo-color": "#05080f", "text-halo-width": 1.6 } });
}

export function renderStorms(map: MlMap, data: StormsResp | null): void {
  // the past-storm player hides every live layer while it runs; live storms that arrive meanwhile (start-up
  // fetch landing late) wait until it is closed, so they never show inside it (v0.9.10a)
  if (document.body.classList.contains("past-mode")) { window.setTimeout(() => renderStorms(map, data), 1000); return; }
  void installStormLayers(map).then(() => paint(map, data));
}

/** Draw the given storms onto the shared map sources (track + cone + spinning current centres).
 *  Does NOT touch `current` or the pill — callers own that, so the global overlay can reuse this
 *  without overwriting the near-Thailand cache the summary/timeline read. */
function paintSources(map: MlMap, storms: Storm[]): void {
  if (!storms.length) {
    for (const id of ["storm-cone", "storm-obs", "storm-fore", "storm-pos", "storm-cur"]) (map.getSource(id) as GeoJSONSource | undefined)?.setData(empty());
    curFeatures = []; stopSpin();
    return;
  }
  const cone: GeoJSON.Feature[] = [], obs: GeoJSON.Feature[] = [], fore: GeoJSON.Feature[] = [], pos: GeoJSON.Feature[] = [], cur: GeoJSON.Feature[] = [];
  for (const s of storms) {
    if (s.cone.length) cone.push({ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: s.cone } });
    if (s.observedPath.length >= 2) obs.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: s.observedPath } });
    if (s.forecastPath.length >= 2) fore.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: s.forecastPath } });
    // a Thai-day label on the first forecast position of each day
    for (const g of dayGroups(s)) pos.push({ type: "Feature", properties: { label: g.label.replace(/ (เช้า|บ่าย|ค่ำ|กลางคืน)$/, "") }, geometry: { type: "Point", coordinates: [g.pos.lon, g.pos.lat] } });
    if (s.current) cur.push({ type: "Feature", properties: { label: `🌀 ${s.name.replace(/-\d+$/, "")}`, rot: spinAngle }, geometry: { type: "Point", coordinates: s.current } });
  }
  (map.getSource("storm-cone") as GeoJSONSource).setData(fc(cone));
  (map.getSource("storm-obs") as GeoJSONSource).setData(fc(obs));
  (map.getSource("storm-fore") as GeoJSONSource).setData(fc(fore));
  (map.getSource("storm-pos") as GeoJSONSource).setData(fc(pos));
  (map.getSource("storm-cur") as GeoJSONSource).setData(fc(cur));
  curFeatures = cur; startSpin(map); // rotate the current-centre icons while they are on the map
}

function paint(map: MlMap, data: StormsResp | null): void {
  current = data;
  const storms = data?.storms ?? [];
  paintSources(map, storms);
  if (!storms.length || !pillEnabled) hidePill();
  else showPill(map, data!);
}

/* ── v0.9.13 spin loop — rotates only the storm-cur source each frame (small, few features) ── */
function startSpin(map: MlMap): void {
  if (spinRaf || prefersReducedMotion() || !curFeatures.length) return;
  const step = (ts: number): void => {
    const dt = spinLast ? ts - spinLast : 0; spinLast = ts;
    spinAngle = (spinAngle - dt * 0.12) % 360; // counter-clockwise (northern hemisphere), same rate as the past-storm player
    const src = map.getSource("storm-cur") as GeoJSONSource | undefined;
    if (src && curFeatures.length) { for (const f of curFeatures) (f.properties as Record<string, unknown>).rot = spinAngle; src.setData(fc(curFeatures)); }
    spinRaf = requestAnimationFrame(step);
  };
  spinRaf = requestAnimationFrame(step);
}
function stopSpin(): void { if (spinRaf) cancelAnimationFrame(spinRaf); spinRaf = 0; spinLast = 0; }

function setCurrentIcon(map: MlMap, lonlat: [number, number], label: string): void {
  (map.getSource("storm-cur") as GeoJSONSource | undefined)?.setData(fc([{ type: "Feature", properties: { label }, geometry: { type: "Point", coordinates: lonlat } }]));
}

function fitStorms(map: MlMap, storms: Storm[]): void {
  const pts: [number, number][] = [];
  for (const s of storms) { pts.push(...s.observedPath, ...s.forecastPath); if (s.current) pts.push(s.current); }
  if (!pts.length) return;
  const b = new maplibregl.LngLatBounds(pts[0]!, pts[0]!);
  for (const p of pts) b.extend(p);
  map.fitBounds(b, { padding: 70, maxZoom: 6.5, duration: 700 });
}

function showPill(map: MlMap, data: StormsResp): void {
  hidePill();
  const names = data.storms.map((x) => x.name.replace(/-\d+$/, "")).join(", ");
  // "อีก N วันพายุเข้า" when a forecast position enters Thailand
  let arrive = "";
  for (const s of data.storms) {
    const g = dayGroups(s).find((x) => x.enters);
    if (g) { const n = Math.round((new Date(g.thaiDate).getTime() - new Date(thaiToday(new Date())).getTime()) / 86400000); arrive = n <= 0 ? "พายุเข้าเขตไทยแล้ว" : `อีก ${n} วันพายุเข้า`; break; }
  }
  const el = document.createElement("button");
  el.className = "storm-pill";
  const upd = data.storms[0]!.updatedAt ? new Date(data.storms[0]!.updatedAt!).toLocaleString("th-TH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  // prominent status line: red when a track reaches Thailand, otherwise the plain reassurance
  // "ยังไม่มีรายงานเข้าไทย" (owner, task #1705 — warn/aware, but state clearly it is not a Thailand hit)
  const note = arrive || "ยังไม่มีรายงานเข้าไทย";
  el.innerHTML = `🌀 <b>พายุ ${names}</b> <span class="sp-note${arrive ? " warn" : ""}">${note}</span> <span class="sp-sub">แตะดูเส้นทาง</span>`;
  el.addEventListener("click", () => { fitStorms(map, data.storms); showDetails(map, data); });
  document.body.appendChild(el);
  pill = el;
  const cr = document.createElement("div"); cr.className = "storm-credit";
  cr.textContent = `ข้อมูลพายุ: ${data.source}${upd ? ` · ${upd}` : ""}`;
  el.appendChild(cr);
}

function showDetails(map: MlMap, data: StormsResp, repaint: () => void = () => paint(map, data)): void {
  const existing = document.querySelector(".storm-sheet"); if (existing) { existing.remove(); return; }
  const s = data.storms[0]!;
  const wind = s.positions.find((p) => p.windKmh != null)?.windKmh ?? null;
  const groups = dayGroups(s);
  const enters = groups.some((g) => g.enters);
  // a "far" global storm (outside the approach box) carries only its current centre — no forecast
  // track/cone in our data — so show its position + distance, not an empty day selector.
  const far = s.status === "far" || (!groups.length && !s.observedPath.length && !s.forecastPath.length);
  const sheet = document.createElement("div");
  sheet.className = "storm-sheet";
  const chips = groups.map((g, i) => `<button class="sd-day${i === 0 ? " sel" : ""}${g.enters ? " enters" : ""}" data-lon="${g.pos.lon}" data-lat="${g.pos.lat}" data-label="${g.label}">${g.label.split(" ").slice(0, 3).join(" ")}${g.enters ? "<span class=\"sd-in\">พายุเข้า</span>" : ""}</button>`).join("");
  const dir = s.current ? dirFromThailand(s.current[0], s.current[1]) : null;
  sheet.innerHTML = `<div class="ss-head">🌀 พายุ ${s.name.replace(/-\d+$/, "")} <button class="ss-x" aria-label="ปิด">✕</button></div>
    <div class="ss-note${enters ? " warn" : ""}">${enters ? "มีรายงานพายุเข้าเขตไทย" : far ? "อยู่ไกลจากไทย ไม่มีผลต่อไทย" : "ยังไม่มีรายงานเข้าไทย"}</div>
    <div class="ss-row">${s.category ? s.category : "พายุหมุนเขตร้อน"}${wind != null ? ` · ลมสูงสุด ~${wind} กม./ชม.` : ""} · ห่างไทย ~${s.nearestKm.toLocaleString("th-TH")} กม.${far && dir ? ` · ทาง${dir}` : ""}</div>
    ${far
      ? `<div class="ss-row muted">พายุลูกนี้อยู่นอกภูมิภาคเอเชียตะวันออกเฉียงใต้ · แสดงเฉพาะตำแหน่งปัจจุบัน (ยังไม่มีเส้นทางพยากรณ์ในระบบ)</div>`
      : `<div class="ss-row muted">เส้นทึบ = ที่ผ่านมา · เส้นประม่วง = พยากรณ์ · โซนม่วง = กรวยความไม่แน่นอน</div>
    <div class="ss-days">${chips}</div>
    <div class="ss-row muted" id="sd-dir"></div>`}
    <div class="ss-row muted small">ข้อมูลพายุ: ${data.source}${data.license ? ` · ${data.license}` : ""}${s.updatedAt ? ` · อัปเดต ${new Date(s.updatedAt).toLocaleString("th-TH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</div>`;
  sheet.querySelector(".ss-x")?.addEventListener("click", () => { sheet.remove(); repaint(); });
  const selectDay = (btn: HTMLElement): void => {
    sheet.querySelectorAll(".sd-day").forEach((b) => b.classList.remove("sel"));
    btn.classList.add("sel");
    const lon = Number(btn.dataset.lon), lat = Number(btn.dataset.lat), label = btn.dataset.label ?? "";
    setCurrentIcon(map, [lon, lat], `🌀 ${label}`);
    const dirEl = sheet.querySelector("#sd-dir")!;
    dirEl.textContent = inTH(lon, lat) ? "ตำแหน่งวันนี้อยู่ในเขตไทย" : `เข้ามาจากทาง${dirFromThailand(lon, lat)}`;
    map.easeTo({ center: [lon, lat], duration: 500 });
  };
  sheet.querySelectorAll<HTMLElement>(".sd-day").forEach((b) => b.addEventListener("click", () => selectDay(b)));
  document.body.appendChild(sheet);
  const first = sheet.querySelector<HTMLElement>(".sd-day"); if (first) selectDay(first);
}

function hidePill(): void { pill?.remove(); pill = null; document.querySelector(".storm-sheet")?.remove(); }
export function stormsLoaded(): StormsResp | null { return current; }

/* v0.9.12 — the live storm overlay is drawn on demand (the "ดูสภาพอากาศ" button), not at
 * start-up, so one heavy render never blocks the first screen ("แยกกดทีละเรื่อง ไม่ให้ค้าง"). */
/** Draw the cached live storms (track + cone + spinning icons). Nothing cached / no storm → nothing drawn.
 *  withPill=false when the "พายุรอบโลก" panel is the list (it replaces the floating pill). */
export function showStormOverlay(map: MlMap, withPill = true): void { pillEnabled = withPill; renderStorms(map, current); }
/** Clear the storm track/cone/pill from the map WITHOUT dropping the cached data
 *  (the summary row + the ▶ timeline keep working). */
export function hideStormOverlay(map: MlMap): void {
  pillEnabled = true;
  stopSpin(); curFeatures = [];
  for (const id of ["storm-cone", "storm-obs", "storm-fore", "storm-pos", "storm-cur"]) (map.getSource(id) as GeoJSONSource | undefined)?.setData(empty());
  hidePill();
}

/* ── v0.9.16 "🌀 พายุรอบโลก" panel (task #1722) ───────────────────────────────────────────────
 * The top-right 🌀 button opens this. It now draws EVERY active tropical cyclone on Earth from GDACS
 * (/api/storms?scope=global), not just those near Thailand — storms in the approach box show their
 * full track/cone, distant ones show a spinning current centre. Each is labelled near/watch/far, with
 * a clear banner on whether any actually reaches Thailand. The historical track simulation is nested
 * one level deeper via an opt-in toggle. No cyclone anywhere → says so plainly. */
async function loadGlobalStorms(): Promise<StormsResp | null> {
  try { const r = await fetch("/api/storms?scope=global"); if (r.ok) globalData = (await r.json()) as StormsResp; } catch { /* keep any previous payload */ }
  return globalData;
}

/** Draw the given (global) storms onto the shared map sources WITHOUT touching `current` or the pill. */
function paintGlobalOverlay(map: MlMap, data: StormsResp | null): void {
  if (document.body.classList.contains("past-mode")) { window.setTimeout(() => paintGlobalOverlay(map, data), 1000); return; }
  pillEnabled = false;
  void installStormLayers(map).then(() => paintSources(map, data?.storms ?? []));
}

export function openGlobalStorms(map: MlMap, opts: { onSimulate?: () => void; simAvailable?: boolean } = {}): void {
  // show whatever we already have (or a loading state) immediately, then refresh from the global feed
  renderGlobalStorms(map, opts, globalData == null);
  void loadGlobalStorms().then(() => { if (document.querySelector(".gs-panel")) renderGlobalStorms(map, opts, false); });
}

function renderGlobalStorms(map: MlMap, opts: { onSimulate?: () => void; simAvailable?: boolean }, loading: boolean): void {
  document.querySelector(".gs-panel")?.remove();
  const data = globalData;
  paintGlobalOverlay(map, data); // draw the world's storms (spinning + named) without the floating pill
  const storms = [...(data?.storms ?? [])].sort((a, b) => a.nearestKm - b.nearestKm);
  const box = document.createElement("div");
  box.className = "gs-panel"; box.setAttribute("role", "dialog"); box.setAttribute("aria-label", "พายุรอบโลก");
  const upd = storms[0]?.updatedAt ? new Date(storms[0]!.updatedAt!).toLocaleString("th-TH", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  let listHtml: string;
  let noteHtml = "";
  if (loading && !storms.length) {
    listHtml = `<div class="gs-empty muted">กำลังโหลดพายุทั่วโลก…</div>`;
  } else if (!storms.length) {
    listHtml = `<div class="gs-empty muted">ขณะนี้ไม่พบพายุหมุนเขตร้อนที่ยังมีผลอยู่ทั่วโลก</div>`;
  } else {
    const anyEnters = storms.some(entersTH);
    // prominent banner: reassuring (green) when no track reaches Thailand, red when one does
    noteHtml = `<div class="gs-note${anyEnters ? " warn" : ""}">${anyEnters ? "⚠ มีรายงานพายุเข้าเขตไทย" : "ยังไม่มีรายงานพายุเข้าไทย"}</div>`;
    listHtml = storms.map((s, i) => {
      const nm = s.name.replace(/-\d+$/, "");
      const dir = s.current ? dirFromThailand(s.current[0], s.current[1]) : null;
      const enters = entersTH(s);
      const badge = enters ? `<span class="gs-in">มีผลต่อไทย</span>`
        : s.status === "watch" ? `<span class="gs-watch">เฝ้าระวัง</span>`
        : s.status === "far" ? `<span class="gs-far">ไกลจากไทย</span>` : "";
      const wind = s.positions.find((p) => p.windKmh != null)?.windKmh ?? null;
      const sub = `ห่างไทย ~${s.nearestKm.toLocaleString("th-TH")} กม.${dir ? ` · ทาง${dir}` : ""}${wind != null ? ` · ลม ~${wind} กม./ชม.` : ""}`;
      return `<button class="gs-item" data-i="${i}"><span class="gs-ico" aria-hidden="true">🌀</span><span class="gs-body"><span class="gs-nm">${escHtml(nm)}${badge}</span><span class="gs-sub">${sub}</span></span></button>`;
    }).join("");
  }
  box.innerHTML = `<div class="gs-head"><b>🌀 พายุรอบโลก</b><button class="gs-x" aria-label="ปิด">✕</button></div>
    <div class="muted small">พายุหมุนเขตร้อนที่ยังมีผลอยู่ทั่วโลก · หมุนบนแผนที่พร้อมชื่อลูกพายุ · ป้ายบอกระยะห่างจากไทย</div>
    ${noteHtml}
    <div class="gs-list">${listHtml}</div>
    ${data ? `<div class="muted small gs-src">ข้อมูลพายุ: ${escHtml(data.source)}${data.license ? ` · ${escHtml(data.license)}` : ""}${upd ? ` · อัปเดต ${upd}` : ""}</div>` : ""}
    ${opts.simAvailable ? `<div class="gs-sim"><button class="gs-sim-btn" aria-label="จำลองเส้นทางพายุที่เคยเข้าไทย (ข้อมูลย้อนหลัง)">▶ จำลองเส้นทางพายุที่เคยเข้าไทย <span class="muted">(ข้อมูลย้อนหลัง)</span></button></div>` : ""}`;
  const close = (): void => { box.remove(); hideStormOverlay(map); };
  box.querySelector(".gs-x")?.addEventListener("click", close);
  box.querySelectorAll<HTMLElement>(".gs-item").forEach((b) => b.addEventListener("click", () => {
    const s = storms[Number(b.dataset.i)]; if (!s || !data) return;
    fitStorms(map, [s]); showDetails(map, { ...data, storms: [s] }, () => paintGlobalOverlay(map, globalData));
  }));
  box.querySelector(".gs-sim-btn")?.addEventListener("click", () => { box.remove(); hideStormOverlay(map); opts.onSimulate?.(); });
  document.body.append(box);
}

/** v0.9.6 B2 — each live storm as a timed track for the ▶ timeline: the GDACS positions that
 *  carry a time, in time order (observed + forecast as GDACS gives them — never extended). */
export interface TimedStorm { name: string; points: { t: number; lon: number; lat: number; windKmh: number | null; forecast: boolean }[] }
export function stormTimeline(): TimedStorm[] {
  const out: TimedStorm[] = [];
  for (const s of current?.storms ?? []) {
    const pts = s.positions.map((p) => ({ p, d: parseLabel(p.time, s.updatedAt) }))
      .filter((x): x is { p: StormPos; d: Date } => x.d != null)
      .map(({ p, d }) => ({ t: d.getTime(), lon: p.lon, lat: p.lat, windKmh: p.windKmh, forecast: p.forecast }))
      .sort((a, b) => a.t - b.t);
    if (pts.length >= 2) out.push({ name: s.name, points: pts });
  }
  return out;
}
